/**
 * Split rules inside a virtual account (TMMAPP-0112; TMMAPI-0148, MM-D-123) — generated wire types in,
 * normalized domain out. A rule re-splits an incoming positive Unassigned part: it matches on the
 * served name, the contributor and a minimum amount (at least one), then moves FIXED amounts, then
 * PERCENT shares, to envelopes and / or categories; the rest stays in Unassigned. Every member reads
 * the rules; the owner or a Manager writes them. An update is a `PUT` replace (the rule and its
 * targets are one value). The caller refetches after a write.
 */
import { request } from './client';
import { personNameOrNull } from './personNames';
import type {
  SplitRuleReorderRequest,
  SplitRuleRequest,
  SplitRuleResponse as WireRule,
  SplitRuleTargetView as WireTarget,
} from './generated';

export type SplitTargetMode = 'FIXED' | 'PERCENT';

export interface SplitRuleTarget {
  envelopeId: number | null;
  /** Served with the envelope id; `null` for a category-only target. */
  envelopeName: string | null;
  categoryId: number | null;
  mode: SplitTargetMode;
  /** FIXED only, minor units of the virtual account owner's currency. */
  amountMinor: number | null;
  /** PERCENT only, in basis points (1–10000). */
  bp: number | null;
}

export interface SplitRule {
  id: number;
  name: string;
  priority: number;
  enabled: boolean;
  /** The served name it matches (trimmed, case-insensitive exact, server-side). */
  matchName: string | null;
  matchContributorUserId: number | null;
  /** The contributor's public name, served with the id. */
  matchContributorName: string | null;
  minAmountMinor: number | null;
  targets: SplitRuleTarget[];
  /** The rule's author (TMMAPI-0152 ask 4). */
  authorUserId: number;
  /** The author's public name, never an email. */
  authorName: string | null;
  /** The author can no longer allocate here (MM-D-123): the engine skips the rule; it still lists. */
  authorityLost: boolean;
}

export interface SplitRuleTargetInput {
  envelopeId?: number | undefined;
  categoryId?: number | undefined;
  mode: SplitTargetMode;
  amountMinor?: number | undefined;
  bp?: number | undefined;
}

export interface SplitRuleInput {
  name: string;
  enabled: boolean;
  matchName?: string | undefined;
  matchContributorUserId?: number | undefined;
  minAmountMinor?: number | undefined;
  priority?: number | undefined;
  targets: SplitRuleTargetInput[];
}

const MODES: ReadonlySet<string> = new Set(['FIXED', 'PERCENT']);

/** A served name, trimmed; a blank one is none. */
const nameOrNull = (v: string | undefined): string | null =>
  v !== undefined && v.trim().length > 0 ? v : null;

/** A target; `null` (dropped) for a mode this build doesn't know. */
function normalizeTarget(w: WireTarget): SplitRuleTarget | null {
  if (!MODES.has(w.mode)) return null;
  return {
    envelopeId: w.envelopeId ?? null,
    envelopeName: nameOrNull(w.envelopeName),
    categoryId: w.categoryId ?? null,
    mode: w.mode,
    amountMinor: w.mode === 'FIXED' ? (w.amountMinor ?? null) : null,
    bp: w.mode === 'PERCENT' ? (w.bp ?? null) : null,
  };
}

export function normalizeSplitRule(w: WireRule): SplitRule {
  return {
    id: w.id,
    name: w.name,
    priority: w.priority,
    enabled: w.enabled,
    matchName: nameOrNull(w.matchName),
    matchContributorUserId: w.matchContributorUserId ?? null,
    matchContributorName: nameOrNull(w.matchContributorName),
    minAmountMinor: w.minAmountMinor ?? null,
    targets: w.targets.flatMap((row) => {
      const target = normalizeTarget(row);
      return target ? [target] : [];
    }),
    authorUserId: w.authorUserId,
    authorName: personNameOrNull(w.authorName),
    authorityLost: w.authorityLost,
  };
}

/** The rules in priority order (the order they are tried). */
export function normalizeSplitRuleList(value: readonly WireRule[]): SplitRule[] {
  return value.map(normalizeSplitRule).sort((a, b) => a.priority - b.priority);
}

/** The request body: an absent or blank match field is not sent. */
export function toSplitRuleRequest(input: SplitRuleInput): SplitRuleRequest {
  const matchName = input.matchName?.trim() ?? '';
  const body: SplitRuleRequest = {
    name: input.name.trim(),
    enabled: input.enabled,
    targets: input.targets.map((target) => {
      const wire: SplitRuleRequest['targets'][number] = { mode: target.mode };
      if (target.envelopeId !== undefined) wire.envelopeId = target.envelopeId;
      if (target.categoryId !== undefined) wire.categoryId = target.categoryId;
      if (target.mode === 'FIXED' && target.amountMinor !== undefined) {
        wire.amountMinor = target.amountMinor;
      }
      if (target.mode === 'PERCENT' && target.bp !== undefined) wire.bp = target.bp;
      return wire;
    }),
  };
  if (matchName.length > 0) body.matchName = matchName;
  if (input.matchContributorUserId !== undefined) {
    body.matchContributorUserId = input.matchContributorUserId;
  }
  if (input.minAmountMinor !== undefined) body.minAmountMinor = input.minAmountMinor;
  if (input.priority !== undefined) body.priority = input.priority;
  return body;
}

const rules = (virtualAccountId: number) => `/v1/virtual-accounts/${virtualAccountId}/split-rules`;

export const splitRulesApi = {
  /** GET /v1/virtual-accounts/{id}/split-rules — every member reads them. */
  async list(virtualAccountId: number): Promise<SplitRule[]> {
    return normalizeSplitRuleList(await request<WireRule[]>('GET', rules(virtualAccountId)));
  },
  /** POST …/split-rules. */
  async create(virtualAccountId: number, input: SplitRuleInput): Promise<SplitRule> {
    return normalizeSplitRule(
      await request<WireRule>('POST', rules(virtualAccountId), {
        json: toSplitRuleRequest(input),
      }),
    );
  },
  /** PUT …/split-rules/{ruleId} — a replace. */
  async update(
    virtualAccountId: number,
    ruleId: number,
    input: SplitRuleInput,
  ): Promise<SplitRule> {
    return normalizeSplitRule(
      await request<WireRule>('PUT', `${rules(virtualAccountId)}/${ruleId}`, {
        json: toSplitRuleRequest(input),
      }),
    );
  },
  /** DELETE …/split-rules/{ruleId}. */
  async remove(virtualAccountId: number, ruleId: number): Promise<void> {
    await request<unknown>('DELETE', `${rules(virtualAccountId)}/${ruleId}`);
  },
  /** POST …/split-rules/reorder — names exactly the rule set (else `409 REORDER_SET_MISMATCH`). */
  async reorder(virtualAccountId: number, ruleIds: readonly number[]): Promise<SplitRule[]> {
    const body: SplitRuleReorderRequest = { ruleIds: [...ruleIds] };
    return normalizeSplitRuleList(
      await request<WireRule[]>('POST', `${rules(virtualAccountId)}/reorder`, { json: body }),
    );
  },
};
